import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteLoginCookieAndSession,
  getLoginCookieAndSession,
} from "../../utils";
import { RootState } from "../../redux/store";
import { fetchUserByUniqueId } from "../../redux/actions/userActions";
import {
  Container,
  Box,
  Typography,
  Paper,
  LinearProgress,
  Chip,
  Stack,
  Button,
  TextField,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  InputLabel,
  MenuItem,
  Select,
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ScheduleIcon from "@mui/icons-material/Schedule";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { CenteredLoading } from "../../components/common/CenteredLoading";
import { FormActionButton } from "../../components/common/FormActionButton";
import {
  type Step1Form,
  type Step2IndividualForm,
  type Step2BusinessForm,
  type Step2Form,
  type Step3Form,
  type AddressForm,
  type SellerSetupProgress,
  SELLER_SETUP_STORAGE_KEY,
  TOTAL_STEPS,
} from "./seller-setup-types";
import type { StripeAccountStatus, StripeAccountStatusCode } from "./seller-setup-types";
import {
  getOnboardingStepFromStripeRequirements,
  getRequirementMessagesForDisplay,
} from "./seller-setup-stripe";
import {
  createStripeConnectAccount,
  getStripeConnectAccountStatus,
  updateStripeConnectAccountStep1,
  updateStripeConnectAccountStep2,
  updateStripeConnectAccountStep3,
  submitStripeConnectVerification,
  createStripeAccountLink,
  STRIPE_PUBLISHABLE_KEY,
} from "./seller-setup-api";
import { createBankAccountToken } from "./seller-setup-bank-token";
import { notifyStripeAccountStatusChanged } from "../../hooks/useStripeAccountStatus";

const DEFAULT_STEP1: Step1Form = {
  accountType: "individual",
  country: "US",
  email: "",
  industry: "toyg",
  businessWebsite: "",
};

const DEFAULT_ADDRESS: AddressForm = {
  line1: "",
  line2: "",
  city: "",
  state: "",
  postalCode: "",
};

const DEFAULT_STEP2_INDIVIDUAL: Step2IndividualForm = {
  firstName: "",
  lastName: "",
  dob: "",
  phone: "",
  address: { ...DEFAULT_ADDRESS },
};

const DEFAULT_STEP2_BUSINESS: Step2BusinessForm = {
  businessName: "",
  businessAddress: { ...DEFAULT_ADDRESS },
  businessPhone: "",
  taxId: "",
  representative: {
    firstName: "",
    lastName: "",
    dob: "",
    phone: "",
    address: { ...DEFAULT_ADDRESS },
  },
};

const DEFAULT_STEP3: Step3Form = {
  bankAccountConnected: false,
  accountHolderName: "",
};

function loadProgress(): Partial<SellerSetupProgress> | null {
  try {
    const raw = localStorage.getItem(SELLER_SETUP_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as Partial<SellerSetupProgress>;
  } catch {
    return null;
  }
}

function saveProgress(progress: Partial<SellerSetupProgress>) {
  try {
    localStorage.setItem(
      SELLER_SETUP_STORAGE_KEY,
      JSON.stringify({ ...progress, lastSavedAt: Date.now() }),
    );
  } catch {
    // ignore
  }
}

const SellerSetup: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { loading: userLoading, users } = useSelector(
    (state: RootState) => state.users,
  );

  const profileId = getLoginCookieAndSession();
  const userEmail = users?.[0]?.email ?? "";

  const [step, setStep] = useState(1);
  const [step1, setStep1] = useState<Step1Form>({
    ...DEFAULT_STEP1,
    email: userEmail,
  });
  const [step2Individual, setStep2Individual] = useState<Step2IndividualForm>(
    DEFAULT_STEP2_INDIVIDUAL,
  );
  const [step2Business, setStep2Business] = useState<Step2BusinessForm>(
    DEFAULT_STEP2_BUSINESS,
  );
  const [step3, setStep3] = useState<Step3Form>(DEFAULT_STEP3);
  const [step3BankForm, setStep3BankForm] = useState({
    accountHolderName: "",
    accountHolderType: "individual" as "individual" | "company",
    routingNumber: "",
    accountNumber: "",
    ssn_last_4: "",
  });
  const [redirectCountdown, setRedirectCountdown] = useState<number | null>(
    null,
  );
  const [step1Submitting, setStep1Submitting] = useState(false);
  const [step2Submitting, setStep2Submitting] = useState(false);
  const [step3Submitting, setStep3Submitting] = useState(false);
  const [step4Submitting, setStep4Submitting] = useState(false);
  const [accountLinkLoading, setAccountLinkLoading] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);

  const [stripeStatus, setStripeStatus] = useState<StripeAccountStatus | null>(
    null,
  );
  const [stripeLoading, setStripeLoading] = useState(true);
  const [stripeError, setStripeError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [stepErrors, setStepErrors] = useState<Record<string, string>>({});

  const accountStatus: StripeAccountStatusCode = stripeStatus?.status ?? "PENDING";

  const statusChipLabel =
    accountStatus === "ACTIVE" ? "Active (ready to sell)" :
    accountStatus === "UNDER_REVIEW" ? "Under review" :
    accountStatus === "ACTION_REQUIRED" ? "Action required" :
    accountStatus === "RESTRICTED" ? "Restricted" :
    "Pending";

  const statusChipColor =
    accountStatus === "ACTIVE" ? "success" :
    accountStatus === "ACTION_REQUIRED" || accountStatus === "RESTRICTED" ? "warning" :
    "default";

  const fetchStripeStatus = useCallback(async (
    returnFromStripe?: boolean,
    // Background refreshes after a step submit already show their own button
    // spinner — blocking the whole page with CenteredLoading on top of that is
    // what made every "Next" look like a full page reload, unlike "Back" (which
    // never re-fetches at all). Only the very first load should block like this.
    silent?: boolean,
  ) => {
    if (!profileId) return;
    if (!silent) setStripeLoading(true);
    setStripeError(null);
    try {
      const status = await getStripeConnectAccountStatus(Number(profileId), returnFromStripe);
      setStripeStatus(status);
      if (status) {
        // Use onboardingStep from DB as source of truth (no cache / localStorage override)
        if (
          status.onboardingStep != null &&
          status.onboardingStep >= 1 &&
          status.onboardingStep <= TOTAL_STEPS
        ) {
          setStep(status.onboardingStep);
        } else {
          const result = getOnboardingStepFromStripeRequirements(
            status.requirements.currentlyDue,
            status.requirements.pastDue,
            status.disabledReason,
            status.payoutsEnabled,
          );
          if (
            result.status === "action_required" &&
            result.stepNumber >= 1 &&
            result.stepNumber <= 5
          ) {
            setStep(result.stepNumber);
          }
        }
      }
    } catch (e) {
      setStripeError(
        e instanceof Error ? e.message : "Could not load account status",
      );
      setStripeStatus(null);
    } finally {
      setStripeLoading(false);
    }
  }, [profileId]);

  useEffect(() => {
    if (!profileId) {
      deleteLoginCookieAndSession();
      navigate("/");
      return;
    }
    dispatch(fetchUserByUniqueId(Number(profileId)));
  }, [dispatch, navigate, profileId]);

  useEffect(() => {
    if (!profileId || userLoading) return;
    setStep1((s) => ({
      ...s,
      email: userEmail,
      businessWebsite: s.businessWebsite?.trim() || "",
    }));
  }, [userEmail, userLoading, profileId]);

  // Prefill all steps from Stripe account when we have stripeAccountId and accountDetails from API (no cache)
  useEffect(() => {
    if (!stripeStatus?.stripeAccountId || !stripeStatus?.accountDetails) return;
    const d = stripeStatus.accountDetails;
    if (d.step1) {
      const s1 = d.step1;
      setStep1((prev) => ({
        ...DEFAULT_STEP1,
        ...s1,
        email: userEmail,
        industry: s1.industry && s1.industry.trim() ? s1.industry : "toyg",
        businessWebsite:
          s1.businessWebsite &&
          s1.businessWebsite.trim() &&
          s1.businessWebsite.trim() !== getDefaultBusinessWebsite()
            ? s1.businessWebsite
            : "",
      }));
    }
    if (d.step2Individual) {
      const addr = d.step2Individual.address;
      setStep2Individual((prev) => ({
        ...DEFAULT_STEP2_INDIVIDUAL,
        ...prev,
        ...d.step2Individual,
        address: addr
          ? { ...DEFAULT_ADDRESS, ...prev.address, ...addr }
          : prev.address,
      }));
    }
    if (d.step2Business) {
      const rep = d.step2Business.representative;
      setStep2Business((prev) => ({
        ...DEFAULT_STEP2_BUSINESS,
        ...prev,
        ...d.step2Business,
        businessAddress: d.step2Business?.businessAddress
          ? { ...DEFAULT_ADDRESS, ...d.step2Business!.businessAddress }
          : prev.businessAddress,
        representative: rep
          ? {
              ...DEFAULT_STEP2_BUSINESS.representative,
              ...prev.representative,
              ...rep,
              address: rep.address
                ? {
                    ...DEFAULT_ADDRESS,
                    ...prev.representative.address,
                    ...rep.address,
                  }
                : prev.representative.address,
            }
          : prev.representative,
      }));
    }
    if (d.step3) {
      setStep3((prev) => ({ ...prev, ...d.step3 }));
    }
  }, [stripeStatus?.stripeAccountId, stripeStatus?.accountDetails, userEmail]);

  useEffect(() => {
    if (!profileId) return;
    const stepParam = searchParams.get("step");
    const refresh = searchParams.get("refresh");
    const returnFromStripe = stepParam === "5" || refresh === "true";
    if (returnFromStripe) {
      setSearchParams({}, { replace: true });
      setStep(5);
    } else {
      // Restore form data from localStorage only; step is set from API onboardingStep in fetchStripeStatus (no cache)
      const progress = loadProgress();
      if (progress?.step1) {
        setStep1({
          ...DEFAULT_STEP1,
          ...progress.step1,
          email: userEmail,
          industry: progress.step1.industry || "toyg",
          businessWebsite:
            progress.step1.businessWebsite &&
            progress.step1.businessWebsite.trim() &&
            progress.step1.businessWebsite.trim() !==
              getDefaultBusinessWebsite()
              ? progress.step1.businessWebsite
              : "",
        });
      }
      if (progress?.step2Individual)
        setStep2Individual({
          ...DEFAULT_STEP2_INDIVIDUAL,
          ...progress.step2Individual,
        });
      if (progress?.step2Business)
        setStep2Business({
          ...DEFAULT_STEP2_BUSINESS,
          ...progress.step2Business,
        });
      if (progress?.step3) setStep3(progress.step3);
    }
    fetchStripeStatus(returnFromStripe || undefined);
  }, [profileId, fetchStripeStatus, searchParams, setSearchParams, userEmail]);

  useEffect(() => {
    saveProgress({
      step,
      step1,
      step2Individual,
      step2Business,
      step3,
    });
  }, [step, step1, step2Individual, step2Business, step3]);

  // Redirect ACTIVE users away from /seller-setup immediately — but not right after they
  // just submitted step 5 themselves, since that flow already redirects to /account (My Listings)
  // via the countdown below, even if Stripe approved instantly.
  useEffect(() => {
    if (!stripeLoading && accountStatus === "ACTIVE" && !justSubmitted) {
      navigate("/sell", { replace: true });
    }
  }, [stripeLoading, accountStatus, justSubmitted, navigate]);

  // 10-second countdown on step 5 when nothing for the user to do.
  // justSubmitted covers the immediate post-submit race before Stripe responds.
  useEffect(() => {
    const showTimer =
      step === 5 &&
      (justSubmitted ||
        accountStatus === "UNDER_REVIEW" ||
        accountStatus === "PENDING");
    if (!showTimer) {
      setRedirectCountdown(null);
      return;
    }
    setRedirectCountdown(15);
    const interval = setInterval(() => {
      setRedirectCountdown((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(interval);
          navigate("/account", { state: { tab: 3 } }); // 3 = My Listings tab
          return 0;
        }
        return prev - 1;
      });
    }, 1500);
    return () => clearInterval(interval);
  }, [step, justSubmitted, accountStatus, navigate]);

  const handleSaveAndContinueLater = () => {
    saveProgress({ step, step1, step2Individual, step2Business, step3 });
    navigate("/account");
  };

  const getDefaultBusinessWebsite = () =>
    `https://fiddlepiddle.com/u/${profileId}/profile`;

  const validateStep1 = (): boolean => {
    const err: Record<string, string> = {};
    if (!step1.accountType) err.accountType = "Select account type";
    if (!step1.country) err.country = "Select country";
    if (!step1.email?.trim()) err.email = "Email is required";
    if (!step1.industry?.trim()) err.industry = "Select industry";
    const website =
      step1.businessWebsite?.trim() || getDefaultBusinessWebsite();
    if (website) {
      try {
        new URL(website.startsWith("http") ? website : `https://${website}`);
      } catch {
        err.businessWebsite =
          "Enter a valid URL (e.g. example.com or https://example.com)";
      }
    }
    setStepErrors(err);
    return Object.keys(err).length === 0;
  };

  const validateStep2 = (): boolean => {
    const err: Record<string, string> = {};
    if (step1.accountType === "individual") {
      if (!step2Individual.firstName?.trim())
        err.firstName = "First name is required";
      if (!step2Individual.lastName?.trim())
        err.lastName = "Last name is required";
      if (!step2Individual.dob?.trim()) err.dob = "Date of birth is required";
      if (!step2Individual.phone?.trim())
        err.phone = "Phone number is required";
      if (!step2Individual.address.line1?.trim())
        err.addressLine1 = "Address is required";
      if (!step2Individual.address.city?.trim()) err.city = "City is required";
      if (!step2Individual.address.state?.trim())
        err.state = "State is required";
      if (!step2Individual.address.postalCode?.trim())
        err.postalCode = "Postal code is required";
    } else {
      if (!step2Business.businessName?.trim())
        err.businessName = "Business name is required";
      if (!step2Business.businessPhone?.trim())
        err.businessPhone = "Business phone is required";
      if (!step2Business.taxId?.trim()) err.taxId = "Tax ID (EIN) is required";
      if (!step2Business.representative.firstName?.trim())
        err.repFirstName = "Representative first name is required";
      if (!step2Business.representative.lastName?.trim())
        err.repLastName = "Representative last name is required";
      if (!step2Business.representative.dob?.trim())
        err.repDob = "Representative DOB is required";
      if (!step2Business.representative.phone?.trim())
        err.repPhone = "Representative phone is required";
      if (!step2Business.businessAddress.line1?.trim())
        err.addressLine1 = "Business address is required";
      if (!step2Business.businessAddress.city?.trim())
        err.city = "City is required";
      if (!step2Business.businessAddress.state?.trim())
        err.state = "State is required";
      if (!step2Business.businessAddress.postalCode?.trim())
        err.postalCode = "Postal code is required";
    }
    setStepErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleContinueStep1 = async () => {
    setSubmitError(null);
    if (!validateStep1() || !profileId) return;
    setStep1Submitting(true);
    try {
      const step1Payload = {
        email: step1.email,
        accountType: step1.accountType,
        country: step1.country,
        industry: step1.industry,
        businessWebsite:
          step1.businessWebsite?.trim() || getDefaultBusinessWebsite(),
      };
      if (stripeStatus?.stripeAccountId) {
        // Account already exists (e.g. user went back and changed accountType) —
        // createStripeConnectAccount is a no-op once stripeAccountId is set, so
        // update the existing account instead of silently ignoring the change.
        await updateStripeConnectAccountStep1(Number(profileId), step1Payload);
      } else {
        await createStripeConnectAccount(Number(profileId), {
          ...step1Payload,
          profileId: profileId,
        });
      }
      await fetchStripeStatus(undefined, true);
      notifyStripeAccountStatusChanged();
      setStep(2);
    } catch (e) {
      setSubmitError(
        e instanceof Error
          ? e.message
          : "Failed to create account. Please try again.",
      );
    } finally {
      setStep1Submitting(false);
    }
  };

  const handleContinueStep2 = async () => {
    setSubmitError(null);
    if (!validateStep2() || !profileId) return;
    setStep2Submitting(true);
    const payload: Step2Form & { accountType: "individual" | "business" } =
      step1.accountType === "individual"
        ? { ...step2Individual, accountType: "individual" }
        : { ...step2Business, accountType: "business" };
    try {
      await updateStripeConnectAccountStep2(Number(profileId), payload);
      await fetchStripeStatus(undefined, true);
      setStep(3);
    } catch (e) {
      setSubmitError(
        e instanceof Error
          ? e.message
          : "Failed to save legal details. Please try again.",
      );
    } finally {
      setStep2Submitting(false);
    }
  };

  const validateStep3 = (): boolean => {
    const err: Record<string, string> = {};
    if (!step3BankForm.accountHolderName?.trim())
      err.accountHolderName = "Account holder name is required";
    if (!step3BankForm.routingNumber?.trim())
      err.routingNumber = "Routing number is required";
    else if (!/^\d{9}$/.test(step3BankForm.routingNumber.replace(/\D/g, "")))
      err.routingNumber = "Routing number must be 9 digits";
    if (!step3BankForm.accountNumber?.trim())
      err.accountNumber = "Account number is required";
    else if (step3BankForm.accountNumber.replace(/\D/g, "").length < 4)
      err.accountNumber = "Enter a valid account number";
    if (!step3BankForm.ssn_last_4?.trim())
      err.ssn_last_4 = "SSN last 4 digits is required";
    else if (!/^\d{4}$/.test(step3BankForm.ssn_last_4.replace(/\D/g, "")))
      err.ssn_last_4 = "Enter exactly 4 digits";
    setStepErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleContinueStep3 = async () => {
    setSubmitError(null);
    if (!profileId || !validateStep3()) return;
    setStep3Submitting(true);
    try {
      const tokenId = await createBankAccountToken(STRIPE_PUBLISHABLE_KEY, {
        accountHolderName: step3BankForm.accountHolderName.trim(),
        accountHolderType: step3BankForm.accountHolderType,
        routingNumber: step3BankForm.routingNumber.replace(/\D/g, ""),
        accountNumber: step3BankForm.accountNumber.replace(/\D/g, ""),
      });
      await updateStripeConnectAccountStep3(Number(profileId), {
        externalAccountToken: tokenId,
        accountHolderName: step3BankForm.accountHolderName.trim(),
        accountHolderType: step3BankForm.accountHolderType,
        ssn_last_4: step3BankForm.ssn_last_4.replace(/\D/g, "").slice(0, 4),
      });
      setStep3((s) => ({ ...s, bankAccountConnected: true }));
      setStep3BankForm({
        accountHolderName: "",
        accountHolderType: "individual",
        routingNumber: "",
        accountNumber: "",
        ssn_last_4: "",
      });
      await fetchStripeStatus(undefined, true);
      setStep(4);
    } catch (e) {
      setSubmitError(
        e instanceof Error
          ? e.message
          : "Failed to save payout details. Please try again.",
      );
    } finally {
      setStep3Submitting(false);
    }
  };

  const handleSubmitStep4 = async () => {
    setSubmitError(null);
    if (!profileId) return;
    setStep4Submitting(true);
    try {
      await submitStripeConnectVerification(Number(profileId));
      await fetchStripeStatus(undefined, true);
      notifyStripeAccountStatusChanged();
      setJustSubmitted(true);
      setStep(5);
    } catch (e) {
      setSubmitError(
        e instanceof Error
          ? e.message
          : "Failed to submit for verification. Please try again.",
      );
    } finally {
      setStep4Submitting(false);
    }
  };

  const goToStep = (s: number) => {
    setStep(s);
    setSubmitError(null);
    setStepErrors({});
  };

  const handleCompleteOnStripe = async () => {
    setSubmitError(null);
    if (!profileId) return;
    setAccountLinkLoading(true);
    try {
      const returnUrl = `${window.location.origin}/seller-setup?step=5&refresh=true`;
      const { url } = await createStripeAccountLink(
        Number(profileId),
        returnUrl,
      );
      window.location.href = url;
    } catch (e) {
      setSubmitError(
        e instanceof Error
          ? e.message
          : "Failed to open verification page. Please try again.",
      );
      setAccountLinkLoading(false);
    }
  };

  if (!profileId) return null;
  if (userLoading || !users?.length) return <CenteredLoading />;
  // Don't show form until we've checked Stripe - avoids flash for verified users
  if (stripeLoading) return <CenteredLoading />;
  if (accountStatus === "ACTIVE") return null;

  return (
    <Container maxWidth="md" sx={{ py: 3, px: isMobile ? 2 : 3 }}>
      {/* Header / progress */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" fontWeight={600} gutterBottom>
          Seller Setup
        </Typography>
        <Typography variant="body1" color="text.secondary" gutterBottom>
          Complete this to list items and receive payouts.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Most sellers finish in a few minutes.
        </Typography>
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          flexWrap="wrap"
          sx={{ mb: 1 }}
        >
          <Typography variant="body2" fontWeight={500}>
            Step {step} of {TOTAL_STEPS}
          </Typography>
          <Chip
            size="small"
            label={statusChipLabel}
            color={statusChipColor}
            icon={
              accountStatus === "UNDER_REVIEW" ||
              accountStatus === "PENDING" ? (
                <ScheduleIcon />
              ) : accountStatus === "ACTION_REQUIRED" ||
                accountStatus === "RESTRICTED" ? (
                <WarningAmberIcon />
              ) : undefined
            }
          />
        </Stack>
        <LinearProgress
          variant="determinate"
          value={(step / TOTAL_STEPS) * 100}
          sx={{ height: 6, borderRadius: 1, mb: 1 }}
        />
      </Box>

      {stripeError && !stripeStatus && step === 1 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You can start your seller setup below. We&apos;ll create your account
          when you continue.
        </Alert>
      )}

      {/* Step 1 — Account type + basics */}
      {step === 1 && (
        <Paper elevation={1} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Choose seller type
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This helps us collect the right verification details.
          </Typography>
          <Stack spacing={2}>
            <FormControl error={!!stepErrors.accountType}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Account type
              </Typography>
              <RadioGroup
                row
                value={step1.accountType}
                onChange={(_, v) =>
                  setStep1((s) => ({
                    ...s,
                    accountType: v as "individual" | "business",
                  }))
                }
              >
                <FormControlLabel
                  value="individual"
                  control={<Radio />}
                  label="Individual"
                />
                <FormControlLabel
                  value="business"
                  control={<Radio />}
                  label="Business"
                />
              </RadioGroup>
            </FormControl>
            <FormControl fullWidth size="small" error={!!stepErrors.country} disabled>
              <InputLabel>Country</InputLabel>
              <Select
                value={step1.country}
                label="Country"
                onChange={(e) =>
                  setStep1((s) => ({ ...s, country: e.target.value }))
                }
              >
                <MenuItem value="US">United States</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              size="small"
              label="Email"
              type="email"
              value={step1.email}
              onChange={(e) =>
                setStep1((s) => ({ ...s, email: e.target.value }))
              }
              error={!!stepErrors.email}
              helperText={stepErrors.email}
              disabled
            />
            <TextField
              fullWidth
              size="small"
              label="Business website"
              type="url"
              placeholder={`https://fiddlepiddle.com/u/${profileId}/profile`}
              value={step1.businessWebsite || ""}
              onChange={(e) =>
                setStep1((s) => ({
                  ...s,
                  businessWebsite: e.target.value.trim(),
                }))
              }
              error={!!stepErrors.businessWebsite}
              helperText={
                stepErrors.businessWebsite ||
                "Optional. Leave blank to default to your profile page."
              }
              inputProps={{ autoComplete: "url" }}
            />
          </Stack>
          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <FormActionButton
              text="Continue"
              onClick={handleContinueStep1}
              loading={step1Submitting}
            />
          </Stack>
          {submitError && (
            <Alert
              severity="error"
              onClose={() => setSubmitError(null)}
              sx={{ mt: 2 }}
            >
              {submitError}
            </Alert>
          )}
        </Paper>
      )}

      {/* Step 2 — Legal identity */}
      {step === 2 && (
        <Paper elevation={1} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Legal details
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This must match your legal documents. It&apos;s required to receive
            payouts.
          </Typography>
          <Typography
            variant="caption"
            display="block"
            color="text.secondary"
            sx={{ mb: 2 }}
          >
            We never share this publicly.
          </Typography>
          {step1.accountType === "individual" ? (
            <Stack spacing={2}>
              <TextField
                fullWidth
                size="small"
                label="Legal first name"
                value={step2Individual.firstName}
                onChange={(e) =>
                  setStep2Individual((s) => ({
                    ...s,
                    firstName: e.target.value,
                  }))
                }
                error={!!stepErrors.firstName}
                helperText={stepErrors.firstName}
              />
              <TextField
                fullWidth
                size="small"
                label="Legal last name"
                value={step2Individual.lastName}
                onChange={(e) =>
                  setStep2Individual((s) => ({
                    ...s,
                    lastName: e.target.value,
                  }))
                }
                error={!!stepErrors.lastName}
                helperText={stepErrors.lastName}
              />
              <TextField
                fullWidth
                size="small"
                label="Date of birth"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={step2Individual.dob}
                onChange={(e) =>
                  setStep2Individual((s) => ({ ...s, dob: e.target.value }))
                }
                error={!!stepErrors.dob}
                helperText={stepErrors.dob}
              />
              <TextField
                fullWidth
                size="small"
                label="Phone number"
                type="tel"
                value={step2Individual.phone}
                onChange={(e) =>
                  setStep2Individual((s) => ({ ...s, phone: e.target.value }))
                }
                error={!!stepErrors.phone}
                helperText={
                  stepErrors.phone || "Required by our payment provider."
                }
                placeholder="+1 234 567 8900"
              />
              <TextField
                fullWidth
                size="small"
                label="Address line 1"
                value={step2Individual.address.line1}
                onChange={(e) =>
                  setStep2Individual((s) => ({
                    ...s,
                    address: { ...s.address, line1: e.target.value },
                  }))
                }
                error={!!stepErrors.addressLine1}
                helperText={stepErrors.addressLine1}
              />
              <TextField
                fullWidth
                size="small"
                label="Address line 2 (optional)"
                value={step2Individual.address.line2 || ""}
                onChange={(e) =>
                  setStep2Individual((s) => ({
                    ...s,
                    address: {
                      ...s.address,
                      line2: e.target.value || undefined,
                    },
                  }))
                }
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="City"
                  value={step2Individual.address.city}
                  onChange={(e) =>
                    setStep2Individual((s) => ({
                      ...s,
                      address: { ...s.address, city: e.target.value },
                    }))
                  }
                  error={!!stepErrors.city}
                  helperText={stepErrors.city}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="State"
                  value={step2Individual.address.state}
                  onChange={(e) =>
                    setStep2Individual((s) => ({
                      ...s,
                      address: { ...s.address, state: e.target.value },
                    }))
                  }
                  error={!!stepErrors.state}
                  helperText={stepErrors.state}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Postal code"
                  value={step2Individual.address.postalCode}
                  onChange={(e) =>
                    setStep2Individual((s) => ({
                      ...s,
                      address: { ...s.address, postalCode: e.target.value },
                    }))
                  }
                  error={!!stepErrors.postalCode}
                  helperText={stepErrors.postalCode}
                />
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={2}>
              <TextField
                fullWidth
                size="small"
                label="Business legal name"
                value={step2Business.businessName}
                onChange={(e) =>
                  setStep2Business((s) => ({
                    ...s,
                    businessName: e.target.value,
                  }))
                }
                error={!!stepErrors.businessName}
                helperText={stepErrors.businessName}
              />
              <TextField
                fullWidth
                size="small"
                label="Business phone"
                type="tel"
                value={step2Business.businessPhone}
                onChange={(e) =>
                  setStep2Business((s) => ({
                    ...s,
                    businessPhone: e.target.value,
                  }))
                }
                error={!!stepErrors.businessPhone}
                helperText={
                  stepErrors.businessPhone ||
                  "Required by our payment provider."
                }
                placeholder="+1 234 567 8900"
              />
              <TextField
                fullWidth
                size="small"
                label="Tax ID (EIN if US)"
                value={step2Business.taxId}
                onChange={(e) =>
                  setStep2Business((s) => ({ ...s, taxId: e.target.value }))
                }
                error={!!stepErrors.taxId}
                helperText={stepErrors.taxId}
              />
              <Typography variant="subtitle2">Business address</Typography>
              <TextField
                fullWidth
                size="small"
                label="Address line 1"
                value={step2Business.businessAddress.line1}
                onChange={(e) =>
                  setStep2Business((s) => ({
                    ...s,
                    businessAddress: {
                      ...s.businessAddress,
                      line1: e.target.value,
                    },
                  }))
                }
                error={!!stepErrors.addressLine1}
                helperText={stepErrors.addressLine1}
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="City"
                  value={step2Business.businessAddress.city}
                  onChange={(e) =>
                    setStep2Business((s) => ({
                      ...s,
                      businessAddress: {
                        ...s.businessAddress,
                        city: e.target.value,
                      },
                    }))
                  }
                  error={!!stepErrors.city}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="State"
                  value={step2Business.businessAddress.state}
                  onChange={(e) =>
                    setStep2Business((s) => ({
                      ...s,
                      businessAddress: {
                        ...s.businessAddress,
                        state: e.target.value,
                      },
                    }))
                  }
                  error={!!stepErrors.state}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Postal code"
                  value={step2Business.businessAddress.postalCode}
                  onChange={(e) =>
                    setStep2Business((s) => ({
                      ...s,
                      businessAddress: {
                        ...s.businessAddress,
                        postalCode: e.target.value,
                      },
                    }))
                  }
                  error={!!stepErrors.postalCode}
                />
              </Stack>
              <Typography variant="subtitle2">Representative</Typography>
              <TextField
                fullWidth
                size="small"
                label="First name"
                value={step2Business.representative.firstName}
                onChange={(e) =>
                  setStep2Business((s) => ({
                    ...s,
                    representative: {
                      ...s.representative,
                      firstName: e.target.value,
                    },
                  }))
                }
                error={!!stepErrors.repFirstName}
                helperText={stepErrors.repFirstName}
              />
              <TextField
                fullWidth
                size="small"
                label="Last name"
                value={step2Business.representative.lastName}
                onChange={(e) =>
                  setStep2Business((s) => ({
                    ...s,
                    representative: {
                      ...s.representative,
                      lastName: e.target.value,
                    },
                  }))
                }
                error={!!stepErrors.repLastName}
                helperText={stepErrors.repLastName}
              />
              <TextField
                fullWidth
                size="small"
                label="Date of birth"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={step2Business.representative.dob}
                onChange={(e) =>
                  setStep2Business((s) => ({
                    ...s,
                    representative: {
                      ...s.representative,
                      dob: e.target.value,
                    },
                  }))
                }
                error={!!stepErrors.repDob}
                helperText={stepErrors.repDob}
              />
              <TextField
                fullWidth
                size="small"
                label="Representative phone"
                type="tel"
                value={step2Business.representative.phone}
                onChange={(e) =>
                  setStep2Business((s) => ({
                    ...s,
                    representative: {
                      ...s.representative,
                      phone: e.target.value,
                    },
                  }))
                }
                error={!!stepErrors.repPhone}
                helperText={
                  stepErrors.repPhone || "Required by our payment provider."
                }
                placeholder="+1 234 567 8900"
              />
              <TextField
                fullWidth
                size="small"
                label="Representative address line 1"
                value={step2Business.representative.address.line1}
                onChange={(e) =>
                  setStep2Business((s) => ({
                    ...s,
                    representative: {
                      ...s.representative,
                      address: {
                        ...s.representative.address,
                        line1: e.target.value,
                      },
                    },
                  }))
                }
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="City"
                  value={step2Business.representative.address.city}
                  onChange={(e) =>
                    setStep2Business((s) => ({
                      ...s,
                      representative: {
                        ...s.representative,
                        address: {
                          ...s.representative.address,
                          city: e.target.value,
                        },
                      },
                    }))
                  }
                />
                <TextField
                  fullWidth
                  size="small"
                  label="State"
                  value={step2Business.representative.address.state}
                  onChange={(e) =>
                    setStep2Business((s) => ({
                      ...s,
                      representative: {
                        ...s.representative,
                        address: {
                          ...s.representative.address,
                          state: e.target.value,
                        },
                      },
                    }))
                  }
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Postal code"
                  value={step2Business.representative.address.postalCode}
                  onChange={(e) =>
                    setStep2Business((s) => ({
                      ...s,
                      representative: {
                        ...s.representative,
                        address: {
                          ...s.representative.address,
                          postalCode: e.target.value,
                        },
                      },
                    }))
                  }
                />
              </Stack>
            </Stack>
          )}
          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <Button variant="outlined" onClick={() => goToStep(1)}>
              Back
            </Button>
            <FormActionButton
              text="Save &amp; continue"
              onClick={handleContinueStep2}
              loading={step2Submitting}
            />
          </Stack>
          {submitError && (
            <Alert
              severity="error"
              onClose={() => setSubmitError(null)}
              sx={{ mt: 2 }}
            >
              {submitError}
            </Alert>
          )}
        </Paper>
      )}

      {/* Step 3 — Payout / bank setup for Connect account */}
      {step === 3 && (
        <Paper elevation={1} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Get paid
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add a bank account for payouts. You can update it later.
          </Typography>
          <Typography
            variant="caption"
            display="block"
            color="text.secondary"
            sx={{ mb: 2 }}
          >
            Payouts are sent to this account once verification is complete. We
            never store your full account number.
          </Typography>
          <Stack spacing={2}>
            <TextField
              fullWidth
              size="small"
              label="Account holder name"
              value={step3BankForm.accountHolderName}
              onChange={(e) =>
                setStep3BankForm((s) => ({
                  ...s,
                  accountHolderName: e.target.value,
                }))
              }
              error={!!stepErrors.accountHolderName}
              helperText={stepErrors.accountHolderName}
              required
              inputProps={{ maxLength: 100 }}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Account holder type</InputLabel>
              <Select
                value={step3BankForm.accountHolderType}
                label="Account holder type"
                onChange={(e) =>
                  setStep3BankForm((s) => ({
                    ...s,
                    accountHolderType: e.target.value as
                      | "individual"
                      | "company",
                  }))
                }
              >
                <MenuItem value="individual">Individual</MenuItem>
                <MenuItem value="company">Company</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              size="small"
              label="Routing number (9 digits)"
              value={step3BankForm.routingNumber}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, "").slice(0, 9);
                setStep3BankForm((s) => ({ ...s, routingNumber: v }));
              }}
              error={!!stepErrors.routingNumber}
              helperText={stepErrors.routingNumber || "US bank routing number"}
              required
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
                maxLength: 9,
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="Account number"
              type="password"
              value={step3BankForm.accountNumber}
              onChange={(e) =>
                setStep3BankForm((s) => ({
                  ...s,
                  accountNumber: e.target.value.replace(/\D/g, ""),
                }))
              }
              error={!!stepErrors.accountNumber}
              helperText={
                stepErrors.accountNumber ||
                "We never store or display this after submission."
              }
              required
              inputProps={{ inputMode: "numeric", maxLength: 17 }}
            />
            <TextField
              fullWidth
              size="small"
              label="SSN last 4 digits"
              type="password"
              value={step3BankForm.ssn_last_4 || ""}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, "").slice(0, 4);
                setStep3BankForm((s) => ({ ...s, ssn_last_4: v }));
              }}
              error={!!stepErrors.ssn_last_4}
              helperText={
                stepErrors.ssn_last_4 ||
                "Required for bank verification (US). We never store full SSN."
              }
              inputProps={{
                inputMode: "numeric",
                maxLength: 4,
                autoComplete: "off",
              }}
            />
            <Alert severity="info">
              Your bank details are sent securely to Stripe and tokenized in the
              browser. We never store your full account or routing numbers on
              our servers.
            </Alert>
          </Stack>
          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => goToStep(2)}
              disabled={step3Submitting}
            >
              Back
            </Button>
            <FormActionButton
              text="Continue"
              onClick={handleContinueStep3}
              loading={step3Submitting}
            />
          </Stack>
          {submitError && (
            <Alert
              severity="error"
              onClose={() => setSubmitError(null)}
              sx={{ mt: 2 }}
            >
              {submitError}
            </Alert>
          )}
        </Paper>
      )}

      {/* Step 4 — Review & submit */}
      {step === 4 && (
        <Paper elevation={1} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Review your info
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Confirm everything is correct before submitting.
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Seller type
            </Typography>
            <Typography variant="body1">
              {step1.accountType === "individual" ? "Individual" : "Business"}
            </Typography>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Business website
            </Typography>
            <Typography
              variant="body1"
              sx={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
            >
              {step1.businessWebsite?.trim() ||
                getDefaultBusinessWebsite() ||
                "—"}
            </Typography>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Legal name
            </Typography>
            <Typography variant="body1">
              {step1.accountType === "individual"
                ? `${step2Individual.firstName} ${step2Individual.lastName}`
                : step2Business.businessName}
            </Typography>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Address
            </Typography>
            <Typography variant="body1">
              {step1.accountType === "individual"
                ? `${step2Individual.address.line1}, ${step2Individual.address.city}, ${step2Individual.address.state} ${step2Individual.address.postalCode}`
                : `${step2Business.businessAddress.line1}, ${step2Business.businessAddress.city}`}
            </Typography>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Bank connected
            </Typography>
            <Typography variant="body1">
              {step3.bankAccountConnected ? "Yes" : "No"}
            </Typography>
          </Box>

          <Typography
            variant="caption"
            display="block"
            color="text.secondary"
            sx={{ mb: 2 }}
          >
            We&apos;ll record your verification and show your status below. No
            redirect — everything stays on this site.
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => goToStep(3)}
              disabled={step4Submitting}
            >
              Back
            </Button>
            <FormActionButton
              text="Submit for verification"
              onClick={handleSubmitStep4}
              loading={step4Submitting}
            />
          </Stack>
          {submitError && (
            <Alert
              severity="error"
              onClose={() => setSubmitError(null)}
              sx={{ mt: 2 }}
            >
              {submitError}
            </Alert>
          )}
        </Paper>
      )}

      {/* Step 5 — Status */}
      {step === 5 &&
        (() => {

          const showTimer =
            justSubmitted ||
            accountStatus === "UNDER_REVIEW" ||
            accountStatus === "PENDING";
          const showAction =
            !justSubmitted &&
            (accountStatus === "ACTION_REQUIRED" ||
              accountStatus === "RESTRICTED");
          const actionMessages = showAction
            ? getRequirementMessagesForDisplay(
                stripeStatus?.requirements?.currentlyDue ?? [],
                stripeStatus?.requirements?.pastDue ?? [],
              )
            : [];

          return (
            <Paper elevation={1} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Setup status
              </Typography>

              {/* UNDER_REVIEW / PENDING / just submitted — show timer */}
              {showTimer && (
                <>
                  <Alert
                    severity="info"
                    icon={<ScheduleIcon />}
                    sx={{ mb: 2 }}
                  >
                    Verification in progress
                  </Alert>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Your information has been submitted. Stripe is reviewing
                    your details.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Check your email or notifications from the account page for
                    updates.
                  </Typography>
                  {redirectCountdown !== null && redirectCountdown > 0 && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      Redirecting to account page in {redirectCountdown}{" "}
                      second{redirectCountdown !== 1 ? "s" : ""}…
                    </Typography>
                  )}
                </>
              )}

              {/* ACTION_REQUIRED / RESTRICTED — show Stripe fix button */}
              {showAction && (
                <>
                  <Alert
                    severity="warning"
                    icon={<WarningAmberIcon />}
                    sx={{ mb: 2 }}
                  >
                    Action required
                  </Alert>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Stripe needs more information to verify your seller account.
                  </Typography>
                  {actionMessages.length > 0 && (
                    <>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        sx={{ mb: 1 }}
                      >
                        What you need to do:
                      </Typography>
                      <Stack
                        component="ul"
                        sx={{ pl: 2.5, mb: 2, "& li": { mb: 0.5 } }}
                      >
                        {actionMessages.map((msg, i) => (
                          <li key={i}>
                            <Typography variant="body2">{msg}</Typography>
                          </li>
                        ))}
                      </Stack>
                    </>
                  )}
                  <FormActionButton
                    text={accountLinkLoading ? "Opening…" : "Complete verification on Stripe"}
                    onClick={handleCompleteOnStripe}
                    loading={accountLinkLoading}
                  />
                </>
              )}
            </Paper>
          );
        })()}
    </Container>
  );
};

export default SellerSetup;
